import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  VStack,
  Select,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import Translate from "../../LanguageTranslation/Translate";
import axiosInstance from "../../../helpers/axiosInstance";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import ReactSelect from "react-select";
import { ChevronDownIcon } from "@chakra-ui/icons";

// Initialize countries with English locale
countries.registerLocale(enLocale);

const BRAND = "#286328";
const dd = {
  variant: "ghost",
  fontWeight: "bold",
  px: 4,
  py: 2,
  border: "1px solid #d8d8d8",
  borderRadius: "md",
  bg: "#FFFBEF",
  _hover: { bg: "#F5F5F5" },
  textAlign: "left",
  justifyContent: "space-between",
};

export default function AccommodationTripDetails({
  accomCountry,
  setAccomCountry,
  accomType,
  setAccomType,
  accomRooms,
  setAccomRooms,
  accomNights,
  setAccomNights,
  accomResult,
  setAccomResult,
  selectedTrip,
  hasTrips,
}) {
  const [loading, setLoading] = useState(false);

  // Prepare countries for react-select
  const countryOptions = useMemo(() => {
    return Object.entries(countries.getNames("en")).map(([code, name]) => ({
      value: name,
      label: name,
    }));
  }, []);

  const calculateAccommodation = async () => {
    // Check if trip is selected / any trips exist
    if (!selectedTrip || selectedTrip.trim() === '') {
      alert(hasTrips
        ? 'Please select a trip before calculating emissions.'
        : 'No trips found. Please create a trip plan first, then select it before calculating emissions.'
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        country: accomCountry,
        accommodation_type: accomType,
        number_of_rooms: Number(accomRooms),
        number_of_nights: Number(accomNights),
      };
      const { data } = await axiosInstance.post(
        "/co2-emissions/calculate/accommodation",
        payload
      );
      setAccomResult(data);
    } catch (err) {
      console.error("Error calculating accommodation emissions:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Column labels */}
      <Flex fontWeight="bold" color="gray.700" mb={3}>
        <Box flex="1">
          <Translate text="Location" />
        </Box>
        <Box flex="1">
          <Translate text="Type" />
        </Box>
        <Box flex="1">
          <Translate text="Details" />
        </Box>
      </Flex>

      {/* Inputs */}
      <Flex gap={6} flexWrap="wrap" mb={12}>
        {/* Location */}
        <Box flex="1" minW="180px">
          <ReactSelect
            placeholder="Select Country"
            value={accomCountry ? { value: accomCountry, label: accomCountry } : null}
            onChange={(option) => setAccomCountry(option?.value || "")}
            options={countryOptions}
            isSearchable={true}
            isClearable={true}
            styles={{
              control: (provided) => ({
                ...provided,
                border: "1px solid #d8d8d8",
                borderRadius: "md",
                backgroundColor: "#FFFBEF",
                minHeight: "40px",
                fontWeight: "bold",
              }),
              menu: (provided) => ({
                ...provided,
                zIndex: 9999,
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? "#286328" : state.isFocused ? "#F5F5F5" : "white",
                color: state.isSelected ? "white" : "black",
              }),
            }}
          />
        </Box>

        {/* Type + Rooms/Nights */}
        <Box flex="1" minW="180px">
          <VStack align="stretch" spacing={4}>
            {/* Accommodation Type */}
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} {...dd}>
                {accomType.charAt(0).toUpperCase() + accomType.slice(1)}
              </MenuButton>
              <MenuList>
                {["hotel", "hostel", "apartment"].map((t) => (
                  <MenuItem key={t} onClick={() => setAccomType(t)}>
                    <Translate text={t.charAt(0).toUpperCase() + t.slice(1)} />
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            {/* Rooms dropdown */}
            <Select
              placeholder="Number of rooms"
              value={accomRooms}
              onChange={(e) => setAccomRooms(e.target.value)}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
            {/* Nights dropdown */}
            <Select
              placeholder="Number of nights"
              value={accomNights}
              onChange={(e) => setAccomNights(e.target.value)}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </VStack>
        </Box>

        {/* Details (unchanged) */}
        <Box flex="1" minW="180px">
          <VStack align="stretch" spacing={4}>
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} {...dd}>
                <Translate text="Service" />
              </MenuButton>
              <MenuList>
                <MenuItem>
                  <Translate text="Breakfast" />
                </MenuItem>
                <MenuItem>
                  <Translate text="All-inclusive" />
                </MenuItem>
              </MenuList>
            </Menu>
            <Button {...dd} isDisabled>
              <Translate text="Rating" />
            </Button>
          </VStack>
        </Box>
      </Flex>

      <Divider mb={8} />

      {/* Calculate */}
      <Button
        w="full"
        bg={BRAND}
        color="white"
        _hover={{ bg: BRAND }}
        borderRadius="lg"
        py={6}
        onClick={calculateAccommodation}
        isLoading={loading}
      >
        <Translate text="Calculate" />
      </Button>
    </>
  );
}
